import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AppSaveBar from '@/components/AppSaveBar/AppSaveBar';
import Layout from '@/components/layout';
import { config } from '@/config';
import { LocationViewChoices, PricingPlan, ProductOverrideType } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { handleScrollToElement, isAvailableFeature, openErrorToast, openToast, stringToArrayByComma, time24h } from '@/helpers';
import {
  useGetDetailLocationSettingQuery,
  useGetGeneralSettingQuery,
  useGetLocationsQuery,
  useUpdateDetailLocationSettingMutation,
} from '@/redux/query';
import { errorFieldsSelector, handleResetErrorFields } from '@/redux/slice/error.slice';
import {
  handleResetPickup,
  handleResetPickupState,
  handleSaveBackupPickupCollections,
  handleSaveBackupPickupHolidays,
  handleSaveBackupPickupProducts,
  handleSaveBackupPickupSettings,
  handleSaveBackupPickupSpecificWorkingDays,
  handleSaveBackupPickupWeeklyWorkingDays,
  handleSaveBackupPickupWeeklyWorkingDaysLastState,
  handleSaveOverridePickupHolidays,
  handleSaveOverridePickupSpecificWorkingDays,
  handleSavePickupCollections,
  handleSavePickupHolidays,
  handleSaveOverridePickupDateRanges,
  handleSavePickupProducts,
  handleSavePickupSettings,
  handleSavePickupSpecificWorkingDays,
  handleSavePickupWeeklyWorkingDays,
  handleSavePickupWeeklyWorkingDaysLastState,
  handleSetPickupState,
  pickupCollectionSelector,
  pickupHolidaySelector,
  pickupProductSelector,
  pickupSettingSelector,
  pickupSpecificWorkingDaySelector,
  pickupWeeklyWorkingDaySelector,
  pickupOverrideSpecificWorkingDaysSelector,
  pickupOverrideHolidaysSelector,
  pickupOverrideDateRangesSelector,
  currentPickupSelector,
  oldPickupSelector,
  handleSaveBackupOverridePickupSpecificWorkingDays,
  handleSaveBackupOverridePickupHolidays,
  handleSaveBackupOverridePickupDateRanges,
  handleResetPickupInitial,
  handleSetPickupDisable,
} from '@/redux/slice/pickup.slice';
import { BlockStack } from '@shopify/polaris';
import { useDispatch, useSelector } from 'react-redux';
import BlackoutDate from './component/BlackoutDates';
import PickupAvailability from './component/PickupAvailability';
import PickupChoice from './component/PickupChoice';
import PickupTags from './component/PickupTags';
import VisualPickupDays from './component/VisualPickupDays';
import SkeletonPage from '@/components/SkeletonPage';
import { handleLocationTab } from '@/redux/slice/tab.slice';
import { ProductAvailability } from './component/ProductAvailability';
import { ProductOverride } from './component/ProductOverride';
import { checkDirtyField } from '@/helpers/compare';
import { DiscardChangeModal } from '@/components';
import { DEFAULT_DATE_RANGE } from '@/constants';
import useTitleBar from '@/hooks/useTitleBar';

const PickupSettingsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id: settingId } = useParams();
  const { refetch: refetchLocationData } = useGetLocationsQuery({ shop: config.shop });
  const {
    data: detailPickupSettingData,
    isLoading: isLoadingPickupSettingData,
    refetch: refetchPickupSettingData,
  } = useGetDetailLocationSettingQuery({ shop: config.shop, id: Number(settingId) });
  const [updatePickupSetting, { isLoading: isLoadingUpdatePickupSetting }] = useUpdateDetailLocationSettingMutation();
  const { data: generalSettingData, isLoading: isLoadingGeneralSettingData } = useGetGeneralSettingQuery(
    { shop: config.shop },
    { refetchOnMountOrArgChange: true },
  );

  const pickupSettings = useSelector(pickupSettingSelector);
  const pickupWeeklyWorkingDays = useSelector(pickupWeeklyWorkingDaySelector);
  const pickupSpecificWorkingDays = useSelector(pickupSpecificWorkingDaySelector);
  const pickupHolidays = useSelector(pickupHolidaySelector);
  const errorFields = useSelector(errorFieldsSelector);
  const pickupProducts = useSelector(pickupProductSelector);
  const pickupCollections = useSelector(pickupCollectionSelector);
  const pickupOverrideSpecificWorkingDays = useSelector(pickupOverrideSpecificWorkingDaysSelector);
  const pickupOverrideHoliday = useSelector(pickupOverrideHolidaysSelector);
  const pickupOverrideDateRange = useSelector(pickupOverrideDateRangesSelector);

  const [isOpenDiscardModal, setOpenDiscardModal] = useState<boolean>(false);
  const currentPickupData = useSelector(currentPickupSelector);
  const oldPickupData = useSelector(oldPickupSelector);

  const isChangeData = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { pickupState: oldPickupState, ...oldRest } = oldPickupData;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { pickupState, ...currentRest } = currentPickupData;
    return checkDirtyField(currentRest, oldRest).isDirty;
  }, [currentPickupData, oldPickupData]);

  const handleDiscard = () => {
    dispatch(handleResetErrorFields());
    dispatch(handleResetPickup());
    setOpenDiscardModal(false);
  };

  const handleLeavePage = () => {
    handleDiscard();
    setTimeout(() => {
      dispatch(handleLocationTab({ key: 'locationTab', value: LocationViewChoices.PICKUP }));
      navigate({ pathname: PATH.LOCATIONS.pathname });
    }, 100);
  };
  const handleStay = () => setOpenDiscardModal(false);

  const handleBackAction = useCallback(() => {
    if (isChangeData) {
      setOpenDiscardModal(true);
    } else {
      handleLeavePage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChangeData]);

  const onSubmit = () => {
    dispatch(handleSetPickupState({ isSaving: true, isLoading: true, isCheckingTimeSlot: true }));
    if (errorFields?.errorFields && Object.values(errorFields?.errorFields).some((item) => item)) {
      dispatch(handleSetPickupState({ isLoading: false }));
      if (errorFields.errorFields?.rule_addTimeSlot) {
        handleScrollToElement('time-slot');
        return;
      }
      if (errorFields.errorFields?.errorSpecificProduct) {
        handleScrollToElement('pickup-specific-product');
        return;
      }
      if (errorFields.errorFields?.errorSpecificDays) {
        handleScrollToElement('pickup-specific-workingdays');
        return;
      }
      if (errorFields.errorFields.emptyDateRange || errorFields.errorFields.emptyProductsAndCollections) {
        handleScrollToElement('pickup-override-rules');
        return;
      }
      return;
    }

    let overrideData;

    if (Number(pickupSettings.overrideType) === ProductOverrideType.OVERRIDE_SPECIFIC_WORKING_DAYS) {
      overrideData = pickupOverrideSpecificWorkingDays.map((item) => ({
        id: item.id,
        settingId: item.settingId,
        productIds: item.productIds,
        collectionIds: item.collectionIds,
        overrideDates: item.overrideDates,
      }));
    } else if (Number(pickupSettings.overrideType) === ProductOverrideType.OVERRIDE_HOLIDAYS) {
      overrideData = pickupOverrideHoliday.map((item) => ({
        id: item.id,
        settingId: item.settingId,
        productIds: item.productIds,
        collectionIds: item.collectionIds,
        overrideDates: item.overrideDates,
      }));
    } else {
      overrideData = pickupOverrideDateRange.map((item) => ({
        id: item.id,
        settingId: item.settingId,
        productIds: item.productIds,
        collectionIds: item.collectionIds,
        overridePreparationDay: item.overridePreparationDay,
        overrideDeliveryDay: item.overrideDeliveryDay,
      }));
    }

    updatePickupSetting({
      shop: pickupSettings.shop,
      id: pickupSettings.id,
      isEnable: pickupSettings.isEnable,
      isDefault: pickupSettings.isDefault,
      dateChoice: pickupSettings.pickupChoice,
      workingDayType: pickupSettings.workingDayType,
      timeSlotType: pickupSettings.pickupTimeSlotType,
      preparationTimeType: pickupSettings.pickupPreparationTimeType,
      orderLimitType: pickupSettings.orderLimitType,
      overrideRuleType: Number(pickupSettings.overrideType),
      productAvailabilityType: pickupSettings.productAvailabilityType,
      dateRange: {
        unit: pickupSettings.pickupDateRange.unit,
        value: Number(pickupSettings.pickupDateRange.value),
      },
      isEnablePreparationTime: pickupSettings.isEnablePreparationTime,
      additionalTags: pickupSettings.additionalTags,
      holidays: pickupHolidays.map((item) => ({
        year: item.year,
        month: item.month,
        dates: item.dates,
      })),
      specificWorkingDays: pickupSpecificWorkingDays.map((item) => ({
        year: item.year,
        month: item.month,
        dates: item.dates,
      })),
      weeklyWorkingDays: pickupWeeklyWorkingDays.map((item) => ({
        isEnable: item.isEnable,
        isEnableCutOffTime: item.isEnableCutOffTime,
        cutOffTime: item.cutOffTime,
        cutOffPreparationTime: {
          unit: item.cutOffPreparationTime?.unit,
          value: Number(item.cutOffPreparationTime?.value),
        },
        day: item.day,
        pickupFrequency: item.pickupFrequency,
        workingHoursStart: item.workingHours?.start,
        workingHoursEnd: item.workingHours?.end,
        preparationTime: {
          unit: item.preparationTime?.unit,
          value: Number(item.preparationTime?.value),
        },
        dateRange: {
          unit: item.dateRange?.unit,
          value: Number(item.dateRange?.value),
        },
        exactTimeSlots: item.exactTimes,
        timeSlots: item.timeSlots.filter((slot) => slot.start && slot.end),
        orderLimitPerDay: item.orderLimitPerDay,
        orderLimitPerTimeSlot: item.workingHours?.orderLimit,
      })),
      productAvailabilityProducts: pickupProducts,
      productAvailabilityCollections: pickupCollections,
      overrideRules: overrideData,
    })
      .unwrap()
      .then(() => {
        refetchPickupSettingData();
        refetchLocationData();
        dispatch(handleResetPickupState());
        openToast(dispatch);
      })
      .catch(() => {
        openErrorToast(dispatch);
        dispatch(handleResetPickupState());
      });
  };

  useEffect(() => {
    if (!detailPickupSettingData?.data) {
      console.log('Data is undefined');
      dispatch(handleResetPickupInitial());
      return;
    }

    const pickupSettingData = {
      id: detailPickupSettingData.data.setting.id,
      shop: detailPickupSettingData.data.setting.shop,
      locationId: detailPickupSettingData.data.setting.locationId,
      isEnable: detailPickupSettingData.data.setting.isEnable,
      isDefault: detailPickupSettingData.data.setting.isDefault,
      pickupChoice: detailPickupSettingData.data.setting.dateChoice,
      workingDayType: detailPickupSettingData.data.setting.workingDayType,
      pickupTimeSlotType: detailPickupSettingData.data.setting.timeSlotType,
      pickupPreparationTimeType: detailPickupSettingData.data.setting.preparationTimeType,
      orderLimitType: detailPickupSettingData.data.setting.orderLimitType,
      overrideType: detailPickupSettingData.data.setting.overrideRuleType,
      pickupDateRange: {
        unit: detailPickupSettingData.data.setting.dateRange?.unit,
        value: detailPickupSettingData.data.setting.dateRange?.value.toString() || DEFAULT_DATE_RANGE.toString(),
      },
      productAvailabilityType: detailPickupSettingData.data.setting.productAvailabilityType,
      isEnablePreparationTime: detailPickupSettingData.data.setting.isEnablePreparationTime,
      additionalTags: stringToArrayByComma(detailPickupSettingData.data.setting.additionalTags),
      updatedAt: detailPickupSettingData.data.setting.updatedAt,
      createdAt: detailPickupSettingData.data.setting.createdAt,
    };

    const pickupWeeklyWorkingDaysData =
      detailPickupSettingData.data.setting.weeklyWorkingDays?.map((item) => ({
        id: item.id,
        settingId: item.settingId,
        isEnable: item.isEnable,
        day: item.day,
        pickupFrequency: item.pickupFrequency,
        workingHours: {
          end: item.workingHoursEnd,
          start: item.workingHoursStart,
          orderLimit: item.orderLimitPerTimeSlot,
        },
        preparationTime: {
          unit: item.preparationTime.unit,
          value: item.preparationTime.value,
        },
        isEnableCutOffTime: item.isEnableCutOffTime,
        cutOffTime: item.cutOffTime,
        cutOffPreparationTime: {
          unit: item.cutOffPreparationTime?.unit,
          value: item.cutOffPreparationTime?.value,
        },
        dateRange: {
          unit: item.dateRange?.unit,
          value: item.dateRange?.value,
        },
        exactTimes: item.exactTimeSlots,
        timeSlots: item.timeSlots,
        orderLimitPerDay: item.orderLimitPerDay,
      })) || [];

    const specificWorkingDaysData =
      detailPickupSettingData.data.setting.specificWorkingDays?.map((item) => ({
        id: item.id,
        settingId: item.settingId,
        year: item.year,
        month: item.month,
        dates: stringToArrayByComma(item.dates).map((date) => Number(date)),
        updatedAt: item.updatedAt,
        createdAt: item.createdAt,
      })) || [];

    const holidayData =
      detailPickupSettingData.data.setting.holidays?.map((item) => ({
        id: item.id,
        settingId: item.settingId,
        year: item.year,
        month: item.month,
        dates: stringToArrayByComma(item.dates).map((date) => Number(date)),
        updatedAt: item.updatedAt,
        createdAt: item.createdAt,
      })) || [];

    const pickupProductData = detailPickupSettingData.data.setting.productAvailabilityProducts;
    const pickupCollectionData = detailPickupSettingData.data.setting.productAvailabilityCollections;

    if (detailPickupSettingData.data.setting.overrideRules) {
      const pickupOverrideDateRule =
        detailPickupSettingData.data.setting.overrideRules?.map((item) => ({
          id: item.id,
          settingId: item.settingId,
          productIds: item.productIds,
          collectionIds: item.collectionIds,
          overrideDates: item.overrideDates,
        })) || [];

      const pickupOverrideTimeRule =
        detailPickupSettingData.data.setting.overrideRules?.map((item) => ({
          id: item.id,
          settingId: item.settingId,
          productIds: item.productIds,
          collectionIds: item.collectionIds,
          overridePreparationDay: item.overridePreparationDay,
          overrideDeliveryDay: item.overrideDeliveryDay,
        })) || [];

      if (detailPickupSettingData.data.setting.overrideRuleType === ProductOverrideType.OVERRIDE_SPECIFIC_WORKING_DAYS) {
        dispatch(handleSaveOverridePickupSpecificWorkingDays(pickupOverrideDateRule));
        dispatch(handleSaveBackupOverridePickupSpecificWorkingDays(pickupOverrideDateRule));
      } else if (detailPickupSettingData.data.setting.overrideRuleType === ProductOverrideType.OVERRIDE_HOLIDAYS) {
        dispatch(handleSaveOverridePickupHolidays(pickupOverrideDateRule));
        dispatch(handleSaveBackupOverridePickupHolidays(pickupOverrideDateRule));
      } else {
        dispatch(handleSaveOverridePickupDateRanges(pickupOverrideTimeRule));
        dispatch(handleSaveBackupOverridePickupDateRanges(pickupOverrideTimeRule));
      }
    }

    dispatch(handleSavePickupSettings(pickupSettingData));
    dispatch(handleSavePickupWeeklyWorkingDays(pickupWeeklyWorkingDaysData));
    dispatch(handleSavePickupWeeklyWorkingDaysLastState(pickupWeeklyWorkingDaysData));
    dispatch(handleSavePickupSpecificWorkingDays(specificWorkingDaysData));
    dispatch(handleSavePickupHolidays(holidayData));
    dispatch(handleSavePickupProducts(pickupProductData));
    dispatch(handleSavePickupCollections(pickupCollectionData));
    dispatch(handleSaveBackupPickupSettings(pickupSettingData));
    dispatch(handleSaveBackupPickupWeeklyWorkingDays(pickupWeeklyWorkingDaysData));
    dispatch(handleSaveBackupPickupWeeklyWorkingDaysLastState(pickupWeeklyWorkingDaysData));
    dispatch(handleSaveBackupPickupSpecificWorkingDays(specificWorkingDaysData));
    dispatch(handleSaveBackupPickupHolidays(holidayData));
    dispatch(handleSaveBackupPickupProducts(pickupProductData));
    dispatch(handleSaveBackupPickupCollections(pickupCollectionData));
    dispatch(handleResetPickupState());
    setOpenDiscardModal(false);
  }, [detailPickupSettingData?.data, detailPickupSettingData?.data.setting, dispatch, generalSettingData?.data.plan]);

  useEffect(() => {
    dispatch(
      handleSetPickupDisable({
        isDisableSpecificProduct: !isAvailableFeature(PricingPlan.Essential, generalSettingData?.data.plan as PricingPlan),
        isDisableProductOverride: !isAvailableFeature(PricingPlan.Smart, generalSettingData?.data.plan as PricingPlan),
        isDisableOrderLimitPerTimeSlot: !isAvailableFeature(PricingPlan.Essential, generalSettingData?.data.plan as PricingPlan),
      }),
    );
  }, [dispatch, generalSettingData?.data.plan]);

  useTitleBar({
    title: 'Pickup Settings',
    breadcrumbLabel: 'Locations',
    breadcrumbPath: PATH.LOCATIONS.pathname,
  });

  return isLoadingPickupSettingData || isLoadingGeneralSettingData ? (
    <SkeletonPage />
  ) : (
    <Layout
      title={detailPickupSettingData?.data.setting.location.name || ''}
      backAction={{
        content: 'Back',
        onAction: handleBackAction,
      }}
    >
      <AppSaveBar open={isChangeData} onSave={onSubmit} onDiscard={handleDiscard} loading={isLoadingUpdatePickupSetting} />
      <BlockStack gap="400">
        <PickupChoice />
        <PickupAvailability time24h={time24h} />
        <VisualPickupDays time24h={time24h} />
        <ProductAvailability />
        <ProductOverride />
        <BlackoutDate />
        <PickupTags />
      </BlockStack>
      <DiscardChangeModal
        isOpen={isOpenDiscardModal}
        onClose={() => setOpenDiscardModal(false)}
        onStay={handleStay}
        onLeave={handleLeavePage}
      />
    </Layout>
  );
};

export default PickupSettingsPage;
